// transcript-steps-dom.ts — THE step timeline's DOM, one implementation for
// every transcript surface in the product.
//
// WHY THIS FILE EXISTS (owner ruling 2026-09-08): "Thinking + tool calls must
// render EXACTLY like the Helm's agent chat transcript." The Helm's collapsed
// step rows lived as private methods of PiMessageView (pi-view.ts) — a 4k-line
// class the assistant overlay cannot instantiate (no pi model, no session, no
// composer). The overlay therefore had its OWN markup for the same idea
// (`.as-card`, `.as-ctx`), which is how the two surfaces diverged in the first
// place. The builders moved HERE verbatim; pi-view.ts and assistant-overlay.ts
// are now two consumers of one module, and a doctored-copy control in the
// gates proves neither host re-implements it.
//
// The state stays with each consumer (pi-view keys expansion off its own Sets;
// the overlay off its own) — this module owns only the DOM shape, the class
// names and the animation hooks, which is exactly what "renders the same" is
// about. Both toggles operate on the LIVE node so a collapse/expand is a class
// flip the CSS grid-rows transition animates, never a rebuild.
//
// The CSS twin is the `transcript-steps-component-block` fence in each host's
// styles.css, mirrored byte-identically (primitives-nav-check arm 7's idiom).
// It speaks `--pi-*`; the assistant overlay feeds those from its own `--as-*`
// seat, so ONE block paints correctly on the chat surface and in the widget.

import { formatChatTime, summarizeArgs } from './pi-live.js';
import { COMPOSER_ICONS, STEP_ICONS } from './pi-steps.js';
import type { PiStep } from './pi-steps.js';

/** "11:31:42 AM" (today) / "Yesterday at 11:31:42 AM" / "<Date> at <Time>" —
 * per-step time in the expanded timeline through the ONE transcript clock
 * (pi-live formatChatTime; seconds kept — a turn's steps usually land within
 * one minute). Both surfaces read the same clock, so a step row cannot say
 * one time in the chat and another in the widget. */
export function fmtStepTime(ts: number): string {
  return formatChatTime(ts, { seconds: true });
}

export interface StepRowOpts {
  /** Shimmer the label — the run is open (a live tool, a thought in flight). */
  running?: boolean;
  isErr?: boolean;
  /** Label override. */
  label?: string;
  /** Entrance animation — only rows NEWER than the last pass get it. */
  anim?: boolean;
  /** Render the detail open at build time (a re-render of an expanded row). */
  expanded?: boolean;
  /** Detail content, called ONLY when the row is built expanded or on first
   * expand — a long transcript must not pay for every collapsed 20k detail. */
  detailNodes?: () => Node[];
}

/**
 * One timeline step: rail (icon + connector) + label row + a lazy,
 * grid-rows-animated detail slot. Moved verbatim from PiMessageView.buildStep;
 * the state it used to read off `this` arrives through `opts`.
 */
export function buildStepRow(s: PiStep, opts: StepRowOpts = {}): HTMLElement {
  const running = opts.running === true;
  const isErr = opts.isErr === true;
  const div = document.createElement('div');
  div.className = `pi-step k-${s.kind}${running ? ' running' : ''}${isErr ? ' iserr' : ''}${opts.anim ? ' pi-anim' : ''}`;
  div.dataset.step = s.key;
  if (s.kind === 'tool' && s.toolId) div.dataset.stepTool = s.toolId;
  const rail = document.createElement('div');
  rail.className = 'pi-step-rail';
  const ico = document.createElement('span');
  ico.className = 'pi-step-ico';
  if (s.icon.startsWith('letter:')) {
    ico.classList.add('letter');
    ico.textContent = s.icon.slice(7, 8);
  } else {
    ico.innerHTML = STEP_ICONS[s.icon] ?? STEP_ICONS.file!;
  }
  rail.append(ico);
  div.append(rail);
  const main = document.createElement('div');
  main.className = 'pi-step-main';
  // Label row: the head left, the step's timestamp right.
  const rowEl = document.createElement('div');
  rowEl.className = 'pi-step-row';
  main.append(rowEl);
  if (s.ts) {
    const time = document.createElement('span');
    time.className = 'pi-step-time';
    time.textContent = fmtStepTime(s.ts);
    rowEl.append(time); // CSS orders it last; appended first so head insertion below stays simple
  }
  // One step's whole ROW rendered as a button (label + chevron, expands the
  // detail in place) — the PR-3 row deferral.
  const head = document.createElement('button');
  head.type = 'button';
  head.dataset.btnRole = 'row';
  head.className = 'pi-step-head';
  head.dataset.stepToggle = s.key;
  const label = document.createElement('span');
  label.className = 'pi-step-label';
  label.textContent = opts.label ?? s.label;
  head.append(label);
  // Hover reveals more of the call than the label carries.
  if (s.kind === 'tool' && s.args !== undefined) {
    const full = typeof s.args === 'string' ? s.args : JSON.stringify(s.args) ?? '';
    const sum = summarizeArgs(s.args);
    const tip = full.length > sum.length ? full : sum;
    if (tip) head.dataset.tip = tip.length > 400 ? `${tip.slice(0, 399)}…` : tip;
  }
  const chev = document.createElement('span');
  chev.className = 'pi-step-chev';
  chev.innerHTML = COMPOSER_ICONS.chevron;
  head.append(chev);
  rowEl.prepend(head);
  const detail = document.createElement('div');
  detail.className = 'pi-step-detail';
  const inner = document.createElement('div');
  inner.className = 'pi-step-detail-in';
  detail.append(inner);
  if (opts.expanded && opts.detailNodes) {
    inner.append(...opts.detailNodes());
    detail.dataset.filled = '1';
    detail.classList.add('open');
    div.classList.add('expanded');
  }
  main.append(detail);
  div.append(main);
  return div;
}

export interface StepsGroup {
  /** The `.pi-steps` element — append it to the transcript. */
  group: HTMLElement;
  /** `.pi-steps-in.pi-tl` — append step rows here (also on a live append). */
  inner: HTMLElement;
  /** `.pi-steps-sum` — patch its textContent as the summary grows. */
  summary: HTMLElement;
}

export interface StepsGroupOpts {
  /** Stable key: the toggle address and the expansion-state key. */
  key: string;
  /** The collapsed one-line summary (composeStepsSummary). */
  summary: string;
  open: boolean;
  /** Shimmer the summary while the run is open. */
  live?: boolean;
}

/**
 * The collapsible group a run of steps folds into — head (summary + chevron)
 * over a grid-rows-animated body. Moved verbatim from the group half of
 * PiMessageView.buildTurnRow.
 */
export function buildStepsGroup(opts: StepsGroupOpts): StepsGroup {
  const group = document.createElement('div');
  group.className = `pi-steps${opts.open ? ' open' : ''}${opts.live ? ' live' : ''}`;
  group.dataset.group = opts.key;
  // The group's whole ROW rendered as a button: it carries the one-line
  // summary and discloses the timeline. Stamped so the primitives gate counts
  // it, never icon-only (the summary IS the content).
  const head = document.createElement('button');
  head.type = 'button';
  head.dataset.btnRole = 'row';
  head.className = 'pi-steps-head';
  head.dataset.stepsToggle = opts.key;
  const sum = document.createElement('span');
  sum.className = 'pi-steps-sum';
  sum.textContent = opts.summary || 'Working…';
  const chev = document.createElement('span');
  chev.className = 'pi-steps-chev';
  chev.innerHTML = COMPOSER_ICONS.chevron;
  head.append(sum, chev);
  const body = document.createElement('div');
  body.className = 'pi-steps-body';
  const inner = document.createElement('div');
  inner.className = 'pi-steps-in pi-tl';
  body.append(inner);
  group.append(head, body);
  return { group, inner, summary: sum };
}

/** Flip a group's timeline open/closed on the LIVE node. Returns the new
 * state, or null when the key names no group under `root`. */
export function toggleStepsGroup(root: HTMLElement, key: string): boolean | null {
  const grp = [...root.querySelectorAll<HTMLElement>('.pi-steps')].find((n) => n.dataset.group === key);
  if (!grp) return null;
  const open = !grp.classList.contains('open');
  grp.classList.toggle('open', open);
  return open;
}

/**
 * Flip one step's detail. Content is built LAZILY on first expand (a long
 * transcript would otherwise pay for every collapsed 20k-char detail at
 * hydration), then animated open on the next frame. Returns the new state, or
 * null when the key names no step under `root`.
 */
export function toggleStepRow(
  root: HTMLElement,
  key: string,
  fill: (key: string) => Node[],
): boolean | null {
  const host = [...root.querySelectorAll<HTMLElement>('.pi-step')].find((n) => n.dataset.step === key);
  const detail = host?.querySelector<HTMLElement>('.pi-step-detail');
  if (!host || !detail) return null;
  if (host.classList.contains('expanded')) {
    detail.classList.remove('open');
    host.classList.remove('expanded');
    return false;
  }
  if (!detail.dataset.filled) {
    detail.querySelector('.pi-step-detail-in')?.append(...fill(key));
    detail.dataset.filled = '1';
  }
  host.classList.add('expanded');
  // Two frames: let the inserted content lay out at 0fr first, so the
  // transition has a start state to animate from.
  requestAnimationFrame(() => requestAnimationFrame(() => detail.classList.add('open')));
  return true;
}
